import { useQuery, useMutation, useSubscription } from '@urql/vue'

import type {
    UseQueryArgs,
    UseQueryResponse,
    UseMutationResponse,
} from '@urql/vue'
import { useInit } from '@/plugins/init/use-init.ts'
import type {
    AnyVariables,
    DocumentInput,
    OperationContext,
    OperationResult,
} from '@urql/core'
import { toValue } from 'vue'
import { useBench } from '@/plugins/bench.ts'

export function useQueryWithInit<
    T = any,
    V extends AnyVariables = AnyVariables,
>(args: UseQueryArgs<T, V>): UseQueryResponse<T, V> {
    const init = useInit()
    const bench = useBench()
    const { isInitialized } = init

    const response = useQuery(args)

    if (QA || import.meta.env.SSR) {
        const queryName = getQueryNameFromQueryArgs(args) ?? 'DataQuery'
        const ben = bench(`Query ${queryName}`)
        response.then(() => {
            ben.end('END')
        })
    }

    if (isInitialized.value) {
        return response
    }

    const then: UseQueryResponse<T, V>['then'] = (onFulfilled, onRejected) => {
        return init.promise.then(async () => {
            return response.then(onFulfilled, onRejected)
        })
    }

    return {
        ...response,
        // oxlint-disable-next-line no-thenable
        then,
    }
}

function useMutationWithQueryLogs<
    T = any,
    V extends AnyVariables = AnyVariables,
>(query: DocumentInput<T, V>): UseMutationResponse<T, V> {
    const response = useMutation(query)
    const bench = useBench()

    if (QA || import.meta.env.SSR) {
        const queryName = getQueryNameFromDocument(query) ?? 'DataMutation'
        const ben = bench(`Mutation ${queryName}`)
        const executeMutation = response.executeMutation

        function executeMutationWrapped(
            variables: V,
            context?: Partial<OperationContext>
        ): Promise<OperationResult<T>> {
            return executeMutation(variables, context)
                .then((r) => {
                    ben.end('END')

                    return r
                })
                .catch((e) => {
                    ben.end('END')

                    throw e
                })
        }

        return {
            ...response,
            executeMutation: executeMutationWrapped,
        }
    }

    return response
}

export {
    useQueryWithInit as useQuery,
    useMutationWithQueryLogs as useMutation,
    useSubscription,
}

export type { UseQueryArgs }

function getQueryNameFromQueryArgs<
    T = any,
    V extends AnyVariables = AnyVariables,
>(args: UseQueryArgs<T, V>): string | undefined {
    return getQueryNameFromDocument(toValue(args.query))
}

function getQueryNameFromDocument<
    T = any,
    V extends AnyVariables = AnyVariables,
>(query: DocumentInput<T, V>): string | undefined {
    if (typeof query !== 'object') {
        return undefined
    }

    if (!('kind' in query)) {
        return undefined
    }

    if ('Document' !== query.kind) {
        return undefined
    }

    const operation = query.definitions.find(
        (d) => d.kind === 'OperationDefinition'
    )

    return operation?.name?.value
}
